import { useEffect, useMemo, useState } from "react";

import { roleLabel, statusLabel } from "../../localization/zh-CN";
import { describeActionError } from "../../core-api/errors";
import {
  assignTriageMemberRole,
  createTriageScope,
  loadTriageAdminDirectory,
  loadTriageInquiries,
  repairTriageRoute,
  revokeTriageMemberRole,
  setTriageMemberStatus,
  updateTriageScope,
  verifyTriageRoute,
  type TriageAdminDirectory,
  type TriageInquiry,
} from "./api";
import "./triage.css";

type AdminTab = "members" | "roles" | "allocation";

export function PeopleWorkAllocation() {
  const [tab, setTab] = useState<AdminTab>("members");
  const [directory, setDirectory] = useState<TriageAdminDirectory>();
  const [routingExceptions, setRoutingExceptions] = useState<TriageInquiry[]>(
    [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = async () => {
    setBusy(true);
    setError("");
    try {
      const [next, noOwner, failed] = await Promise.all([
        loadTriageAdminDirectory(),
        loadTriageInquiries({ exception: "NO_TRIAGE_OWNER" }),
        loadTriageInquiries({ exception: "ROUTING_FAILED" }),
      ]);
      setDirectory(next);
      setRoutingExceptions(
        [...noOwner.items, ...failed.items].filter(
          (item, index, all) =>
            all.findIndex(
              (candidate) => candidate.inquiryId === item.inquiryId,
            ) === index,
        ),
      );
    } catch (cause) {
      setError(describeActionError(cause, "人员与分工加载失败"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
      await refresh();
    } catch (cause) {
      setError(describeActionError(cause, "操作失败，请检查配置后重试"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="triage-workspace people-allocation"
      aria-label="人员与分工"
    >
      <header className="triage-header">
        <div>
          <span className="triage-eyebrow">管理</span>
          <h1>人员与分工</h1>
          <p>
            管理成员、正式权限角色，以及工作空间内站点和来源域名的分诊工作范围。
          </p>
        </div>
        <button type="button" onClick={() => void refresh()} disabled={busy}>
          {busy ? "刷新中…" : "刷新"}
        </button>
      </header>
      <nav className="triage-admin-tabs" aria-label="人员与分工标签">
        {(
          [
            ["members", "成员"],
            ["roles", "权限角色"],
            ["allocation", "询单分工"],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={tab === key ? "is-active" : ""}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {notice ? <div className="triage-feedback success">{notice}</div> : null}
      {error ? <div className="triage-feedback error">{error}</div> : null}
      {!directory && busy ? (
        <div className="triage-empty">正在加载人员与分工…</div>
      ) : null}
      {directory && tab === "members" ? (
        <MembersPanel directory={directory} busy={busy} run={run} />
      ) : null}
      {directory && tab === "roles" ? (
        <RolesPanel directory={directory} busy={busy} run={run} />
      ) : null}
      {directory && tab === "allocation" ? (
        <AllocationPanel
          directory={directory}
          routingExceptions={routingExceptions}
          busy={busy}
          run={run}
        />
      ) : null}
    </main>
  );
}

function MembersPanel({
  directory,
  busy,
  run,
}: {
  directory: TriageAdminDirectory;
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  return (
    <section className="triage-table-card">
      <table>
        <thead>
          <tr>
            <th>成员</th>
            <th>成员资格 / 角色</th>
            <th>负责站点</th>
            <th>接收方式</th>
            <th>路由积压</th>
            <th>最近活动</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {directory.members.map((raw) => {
            const member = raw as unknown as AdminMember;
            const scopes = member.scopes ?? [];
            const routed = (member.routingCounts ?? []).reduce(
              (total, count) => total + Number(count._count?._all ?? 0),
              0,
            );
            return (
              <tr key={member.id}>
                <td>
                  <strong>{member.user.displayName}</strong>
                  <small>{member.user.normalizedEmail}</small>
                </td>
                <td>
                  <strong>{statusLabel(member.status)}</strong>
                  <small>
                    {member.roleAssignments
                      .map((item) => roleLabel(item.role.code))
                      .join("、") || "未分配角色"}
                  </small>
                </td>
                <td>
                  {scopes.map((scope) => scope.site.name).join("、") ||
                    "未配置"}
                </td>
                <td>
                  {scopes.length
                    ? scopes
                        .map(
                          (scope) =>
                            `${scope.assignmentType === "PRIMARY" ? "主负责人" : "备用负责人"} / ${
                              scope.acceptsNewInquiries
                                ? "接收新询单"
                                : "暂停接收"
                            }`,
                        )
                        .join("；")
                    : "—"}
                </td>
                <td>{routed}</td>
                <td>
                  {new Date(member.user.updatedAt).toLocaleString("zh-CN")}
                </td>
                <td>
                  {member.status === "ACTIVE" ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            setTriageMemberStatus(
                              member.userId,
                              "SUSPENDED",
                              "管理员在人员与分工中暂停本地 UAT 成员资格",
                            ),
                          "成员已暂停，下一请求立即失效",
                        )
                      }
                    >
                      暂停
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            setTriageMemberStatus(
                              member.userId,
                              "ACTIVE",
                              "管理员在人员与分工中激活本地 UAT 成员资格",
                            ),
                          "成员已激活",
                        )
                      }
                    >
                      激活
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function RolesPanel({
  directory,
  busy,
  run,
}: {
  directory: TriageAdminDirectory;
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  const [userId, setUserId] = useState("");
  const [roleId, setRoleId] = useState("");
  return (
    <div className="triage-admin-stack">
      <section className="triage-admin-form">
        <h2>分配正式权限角色</h2>
        <label>
          成员
          <select
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">请选择成员</option>
            {directory.members.map((raw) => {
              const member = raw as unknown as AdminMember;
              return (
                <option value={member.userId} key={member.id}>
                  {member.user.displayName} · {member.user.normalizedEmail}
                </option>
              );
            })}
          </select>
        </label>
        <label>
          权限角色
          <select
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
          >
            <option value="">请选择角色</option>
            {directory.roles.map((raw) => {
              const role = raw as unknown as AdminRole;
              return (
                <option value={role.id} key={role.id}>
                  {roleLabel(role.code)}
                </option>
              );
            })}
          </select>
        </label>
        <button
          type="button"
          disabled={busy || !userId || !roleId}
          onClick={() =>
            void run(
              () =>
                assignTriageMemberRole(
                  userId,
                  roleId,
                  "管理员在人员与分工中分配角色",
                ),
              "角色已分配",
            )
          }
        >
          分配角色
        </button>
      </section>
      <section className="triage-table-card">
        <table>
          <thead>
            <tr>
              <th>角色</th>
              <th>代码</th>
              <th>权限数量</th>
              <th>权限摘要</th>
            </tr>
          </thead>
          <tbody>
            {directory.roles.map((raw) => {
              const role = raw as unknown as AdminRole;
              return (
                <tr key={role.id}>
                  <td>
                    <strong>{roleLabel(role.code)}</strong>
                  </td>
                  <td>{role.code}</td>
                  <td>{role.permissions.length}</td>
                  <td>
                    {role.permissions
                      .slice(0, 8)
                      .map((item) => item.permission.key)
                      .join("、")}
                    {role.permissions.length > 8 ? "…" : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className="triage-admin-form">
        <h2>当前成员角色</h2>
        {directory.members.flatMap((raw) => {
          const member = raw as unknown as AdminMember;
          return member.roleAssignments.map((assignment) => (
            <div className="triage-inline-row" key={assignment.id}>
              <span>
                {member.user.displayName} · {roleLabel(assignment.role.code)}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      revokeTriageMemberRole(
                        assignment.id,
                        "管理员在人员与分工中撤销角色",
                      ),
                    "角色已撤销",
                  )
                }
              >
                撤销角色
              </button>
            </div>
          ));
        })}
      </section>
    </div>
  );
}

function AllocationPanel({
  directory,
  routingExceptions,
  busy,
  run,
}: {
  directory: TriageAdminDirectory;
  routingExceptions: TriageInquiry[];
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  const triageMembers = useMemo(
    () =>
      directory.members
        .map((raw) => raw as unknown as AdminMember)
        .filter(
          (member) =>
            member.status === "ACTIVE" &&
            member.user.status === "ACTIVE" &&
            member.roleAssignments.some((assignment) =>
              directory.roles.some((rawRole) => {
                const role = rawRole as unknown as AdminRole;
                return (
                  role.id === assignment.role.id &&
                  role.permissions.some(
                    ({ permission }) =>
                      permission.key === "website_inquiry.dispatch" ||
                      permission.key === "triage.disposition.manage",
                  )
                );
              }),
            ),
        ),
    [directory.members, directory.roles],
  );
  const [siteId, setSiteId] = useState(String(directory.sites[0]?.id ?? ""));
  const site = directory.sites.find((item) => String(item.id) === siteId) as
    | AdminSite
    | undefined;
  const [domainId, setDomainId] = useState(String(site?.domains[0]?.id ?? ""));
  const [userId, setUserId] = useState(triageMembers[0]?.userId ?? "");
  const [assignmentType, setAssignmentType] = useState<"PRIMARY" | "BACKUP">(
    "PRIMARY",
  );
  const [verifyResult, setVerifyResult] = useState<Record<string, unknown>>();
  return (
    <div className="triage-admin-stack">
      {directory.uncoveredDomains.length ? (
        <div className="triage-feedback error">
          有 {directory.uncoveredDomains.length} 个来源域名没有有效
          PRIMARY，请先完成覆盖。
        </div>
      ) : (
        <div className="triage-feedback success">
          所有已注册来源域名均有有效主负责人。
        </div>
      )}
      <section className="triage-admin-form">
        <h2>新增询单分工</h2>
        <label>
          站点
          <select
            value={siteId}
            onChange={(event) => {
              const nextId = event.target.value;
              setSiteId(nextId);
              const next = directory.sites.find(
                (item) => String(item.id) === nextId,
              ) as AdminSite | undefined;
              setDomainId(next?.domains[0]?.id ?? "");
            }}
          >
            {directory.sites.map((raw) => {
              const item = raw as unknown as AdminSite;
              return (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              );
            })}
          </select>
        </label>
        <label>
          来源域名
          <select
            value={domainId}
            onChange={(event) => setDomainId(event.target.value)}
          >
            {(site?.domains ?? []).map((domain) => (
              <option value={domain.id} key={domain.id}>
                {domain.hostname}
              </option>
            ))}
          </select>
        </label>
        <label>
          分诊成员
          <select
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            {triageMembers.map((member) => (
              <option value={member.userId} key={member.userId}>
                {member.user.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          负责方式
          <select
            value={assignmentType}
            onChange={(event) =>
              setAssignmentType(event.target.value as "PRIMARY" | "BACKUP")
            }
          >
            <option value="PRIMARY">主负责人</option>
            <option value="BACKUP">备用负责人</option>
          </select>
        </label>
        <div className="triage-inline-actions">
          <button
            type="button"
            disabled={busy || !siteId || !domainId || !userId}
            onClick={() =>
              void run(
                () =>
                  createTriageScope({
                    siteId,
                    domainId,
                    userId,
                    assignmentType,
                    acceptsNewInquiries: true,
                    activeFrom: new Date().toISOString(),
                    reason: "管理员在人员与分工中创建工作范围",
                  }),
                "询单分工已保存",
              )
            }
          >
            保存分工
          </button>
          <button
            type="button"
            disabled={busy || !siteId || !domainId}
            onClick={() =>
              void run(async () => {
                const result = await verifyTriageRoute({
                  siteId,
                  domainId,
                  receivedAt: new Date().toISOString(),
                });
                setVerifyResult(result);
              }, "只读路由验证已完成")
            }
          >
            验证路由
          </button>
        </div>
        {verifyResult ? (
          <div className="triage-recommendation">
            <strong>
              路由验证结果：{statusLabel(String(verifyResult.status))}
            </strong>
            <span>{String(verifyResult.reason ?? "—")}</span>
            <small>命中规则：{String(verifyResult.matchedRule ?? "—")}</small>
          </div>
        ) : null}
      </section>
      <section className="triage-table-card">
        <table>
          <thead>
            <tr>
              <th>站点 / 来源域名</th>
              <th>负责人</th>
              <th>类型</th>
              <th>状态</th>
              <th>生效时间</th>
              <th>替班</th>
              <th>版本</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {directory.scopes.map((raw) => {
              const scope = raw as unknown as AdminScope;
              return (
                <tr key={scope.id}>
                  <td>
                    <strong>{scope.site.name}</strong>
                    <small>{scope.domain.hostname}</small>
                  </td>
                  <td>{scope.user.displayName}</td>
                  <td>
                    {scope.assignmentType === "PRIMARY"
                      ? "主负责人"
                      : "备用负责人"}
                  </td>
                  <td>
                    {statusLabel(scope.status)} ·{" "}
                    {scope.acceptsNewInquiries ? "接收" : "暂停接收"}
                  </td>
                  <td>{new Date(scope.activeFrom).toLocaleString("zh-CN")}</td>
                  <td>{scope.replacementUser?.displayName ?? "未设置"}</td>
                  <td>v{scope.version}</td>
                  <td>
                    <button
                      type="button"
                      disabled={busy || scope.status !== "ACTIVE"}
                      onClick={() =>
                        void run(
                          () =>
                            updateTriageScope(scope.id, {
                              expectedVersion: scope.version,
                              acceptsNewInquiries: !scope.acceptsNewInquiries,
                              reason: scope.acceptsNewInquiries
                                ? "管理员暂停接收新询单"
                                : "管理员恢复接收新询单",
                            }),
                          scope.acceptsNewInquiries
                            ? "已暂停接收新询单"
                            : "已恢复接收新询单",
                        )
                      }
                    >
                      {scope.acceptsNewInquiries ? "暂停接收" : "恢复接收"}
                    </button>
                    <button
                      type="button"
                      disabled={busy || scope.status === "REVOKED"}
                      onClick={() =>
                        void run(
                          () =>
                            updateTriageScope(scope.id, {
                              expectedVersion: scope.version,
                              status: "REVOKED",
                              reason: "管理员撤销询单分工",
                            }),
                          "询单分工已撤销",
                        )
                      }
                    >
                      撤销
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className="triage-admin-form">
        <h2>无负责人 / 路由失败</h2>
        {routingExceptions.length ? (
          routingExceptions.map((item) => (
            <div className="triage-inline-row" key={item.inquiryId}>
              <span>
                {item.inquiryNumber} · {item.sourceSite.name} ·{" "}
                {item.sourceDomain} ·{" "}
                {statusLabel(item.triageRouting?.failureReason)}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      repairTriageRoute(item.inquiryId, {
                        expectedVersion: item.triageRouting?.version ?? 1,
                        reason: "管理员修复站点和来源域名路由后重新解析",
                      }),
                    "路由已重新解析",
                  )
                }
              >
                修复路由
              </button>
            </div>
          ))
        ) : (
          <p>当前没有无负责人或路由失败的 Inquiry。</p>
        )}
      </section>
      <section className="triage-admin-form">
        <h2>版本历史与审计</h2>
        {directory.audit.slice(0, 12).map((raw) => (
          <div className="triage-inline-row" key={String(raw.id)}>
            <span>
              {new Date(String(raw.occurredAt)).toLocaleString("zh-CN")} ·{" "}
              {String(raw.action)} · {String(raw.reason ?? "无补充原因")}
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}

interface AdminMember {
  id: string;
  userId: string;
  status: string;
  user: {
    id: string;
    displayName: string;
    normalizedEmail: string;
    status: string;
    updatedAt: string;
  };
  roleAssignments: Array<{
    id: string;
    role: { id: string; name: string; code: string };
  }>;
  scopes: AdminScope[];
  routingCounts: Array<{ status: string; _count?: { _all?: number } }>;
}

interface AdminRole {
  id: string;
  code: string;
  name: string;
  permissions: Array<{ permission: { key: string; description: string } }>;
}

interface AdminSite {
  id: string;
  name: string;
  domains: Array<{ id: string; hostname: string }>;
}

interface AdminScope {
  id: string;
  assignmentType: "PRIMARY" | "BACKUP";
  acceptsNewInquiries: boolean;
  activeFrom: string;
  status: string;
  version: number;
  site: { id: string; name: string };
  domain: { id: string; hostname: string };
  user: { id: string; displayName: string };
  replacementUser?: { id: string; displayName: string } | null;
}
